import { useCallback } from 'react';
import { MediaFile } from '../domain/media-file.types';
import { createMediaFileFromFile } from '../domain/media-file.factory';
import { useMedia } from '../media.context';
import { DEFAULT_FILE_SIZE } from '../media.constants';
import { message } from 'components/ui';

export type UseFileSelectionOptions = {
  maxFileSize?: number;
  maxFiles?: number;
  validateFormat?: (mimeType: string) => boolean;
};

export const useFileSelection = (
  onFilesAdded?: (files: MediaFile[]) => void,
  options?: UseFileSelectionOptions
) => {
  const { maxFileSize = DEFAULT_FILE_SIZE, maxFiles, validateFormat } = options || {};
  const { files } = useMedia();

  return useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) {
        return;
      }

      let filesArray = Array.from(fileList);

      // ===== Ограничение по количеству =====
      if (maxFiles) {
        const availableSlots = maxFiles - files.length;

        if (availableSlots <= 0) {
          message({
            type: 'error',
            text: `Нельзя добавить больше файлов (макс. ${maxFiles})`,
          });

          return [];
        }

        if (filesArray.length > availableSlots) {
          const removedFiles = filesArray
            .slice(availableSlots)
            .map((f) => f.name);

          message({
            type: 'error',
            text: `Не удалось добавить следующие файлы: ${removedFiles.join(
              ', '
            )}. Максимальное количество файлов — ${maxFiles}.`,
          });
          filesArray = filesArray.slice(0, availableSlots);
        }
      }

      // ===== Ограничение по размеру =====
      const oversizedFiles = filesArray.filter((f) => f.size > maxFileSize);
      let validFiles = filesArray.filter((f) => f.size <= maxFileSize);

      oversizedFiles.forEach((f) =>
        message({
          type: 'error',
          text: `Файл "${f.name}" слишком большой`,
        })
      );

      // ===== Ограничение по формату =====
      if (validateFormat) {
        const invalidFormatFiles = validFiles.filter((f) => !validateFormat(f.type));
        validFiles = validFiles.filter((f) => validateFormat(f.type));

        if (invalidFormatFiles.length > 0) {
          const invalidNames = invalidFormatFiles.map((f) => f.name);
          message({
            type: 'error',
            text: `Неподдерживаемый формат файла${invalidNames.length > 1 ? 'ов' : ''}: ${invalidNames.join(', ')}`,
          });
        }
      }

      // ===== Ограничение на дубликаты по имени =====
      const existingNames = new Set(files.map((f) => f.name));
      const uniqueFiles: File[] = [];
      const duplicateNames: string[] = [];

      validFiles.forEach((file) => {
        if (existingNames.has(file.name)) {
          duplicateNames.push(file.name);
        } else {
          uniqueFiles.push(file);
          existingNames.add(file.name);
        }
      });

      if (duplicateNames.length) {
        message({
          type: 'error',
          text: `Не удалось добавить следующие файлы: ${duplicateNames.join(
            ', '
          )}. Файлы с такими именами уже существуют.`,
        });
      }

      if (uniqueFiles.length > 0) {
        const mediaFiles = createMediaFileFromFile(uniqueFiles);
        onFilesAdded?.(mediaFiles);

        return mediaFiles;
      }

      return [];
    },
    [files, maxFileSize, maxFiles, validateFormat, onFilesAdded]
  );
};
